/**
 * @file   src\components\Introduction\SendIntroduction.js
 * @brief  This file is responsible for send introduction in right section of want ad details page
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setLocalStorage,
  getLocalStorage,
  getUserId,
  removeLocalStorage,
  loggedInUser,
  thousandSeperatedValue,
  replaceComma,
  trimString,
  createS3Folder,
  upperCaseEachSentenceFirst,
  checkTutorialDisplayStatus,
  saveTutorialStatusToLocal,
  getUserType,
} from "../../helpers/common";
import {
  MEMBER_INTRODUCTION,
  AGENT_INTRODUCTION,
  INTRODUCTION_AMOUNT,
  INTRODUCTION_TAX,
  USER_TYPE_AGENT,
} from "../../constants/common";
import AdIntroIcon from "../../images/ad-intro-icon.svg";
import Button from "react-bootstrap/Button";
import {
  fetchPropertyList,
  fetchInventoryDetailsById,
} from "../../actions/inventory";
import TextBox from "../Common/TextBox";
import { handleTextBoxChange } from "../../actions/common";
import TextArea from "../Common/TextArea";
import PropertySelection from "./PropertySelection";
import ReviewOrderModal from "./ReviewOrderModal";
import { saveIntroductions } from "../../services/introductionService";
import SuccessModal from "./SuccessModal";
import { toast } from "react-toastify";
import { getIntroClients } from "../../actions/agentClients";
import {
  convertToNumber,
  removeNegativeInput,
} from "../../helpers/validations";
import { textValidations } from "../../helpers/validations";
import { ERROR_TEXT } from "../../constants/common";
import { loginOrSignUp } from "../../helpers/message";
import { saveTutorialStatus } from "../../services/commonServices";
import ToolTip from "../Common/ToolTip";
import JoyRide, { STATUS } from "react-joyride";
import DocumentationTutorial from "../Tutorial/DocumentationTutorial";
import IntroTutorialModal from "../Tutorial/IntroTutorialModal";
import IntroductionTutorialIconStep1 from "../../images/tut-property-icon.svg";
import IntroductionTutorialIconStep2 from "../../images/tut-askingprice-icon.svg";
import IntroductionTutorialIconStep3 from "../../images/tut-custommsg-icon.svg";
import IntroductionTutorialIconStep4 from "../../images/tut-send-icon.svg";
import useAgentGitchaRestrictions from "@/hooks/useAgentGitchaRestrictions";
import { getMyInventoryList } from "@/services/inventoryService";
import { limit } from "firebase/firestore";

const SendIntroduction = ({ ...props }) => {
  const tutorialPage = 5; //Want add tutorial
  // Declare action dispatch.
  const dispatch = useDispatch();

  const [blsSentInvIds, setBlsSentInvIds] = useState(new Set());

  const clientList = useSelector((state) => state.agentClients.clients);
  const inventoryDetails = useSelector(
    (state) => state.inventory.inventoryDetails,
  );
  const userDetailsFromStore = useSelector(
    (state) => state.userProfile.userDetails,
  );

  // References
  const messageRef = useRef();

  const [propertyList, setPropertyList] = useState([]);

  useEffect(() => {
    const getUserPropertyList = async () => {
      const response = await getMyInventoryList({
        status: 'published',
        page: 1,
        limit: 100,
        userId: userId,
      });
      const items = response.result;
      // Use first valid photo for PropertyModal/PropertySelection photo check.
      if (Array.isArray(items)) {
        items.forEach((item) => {
          if (!item.photo && Array.isArray(item.photos)) {
            const validPhoto = item.photos.find((p) => p.IsValid);
            if (validPhoto) {
              item.photo = validPhoto.Photo;
            }
          }
        });
      }
      setPropertyList(items);
    }
    getUserPropertyList();
  }, [])


  const propertyListExist =
    (propertyList && propertyList.length > 0) ||
      (propertyList?.defaultProperty && propertyList?.defaultProperty.Inv_Id)
      ? true
      : false;



  const defaultProperty = propertyList?.defaultProperty?.Inv_Id
    ? propertyList?.defaultProperty
    : propertyList && propertyList[0];
  const wantAdId = props.wantAdId;
  const clientListItems = clientList?.ClientList;
  const defaultClient = clientList?.DefaultClient?.ClientId
    ? clientList?.DefaultClient
    : clientList?.ClientList &&
    clientList?.ClientList[clientList?.ClientList.length - 1];
  const [selectedClient, setSelectedClient] = useState("");
  const [displayTutorial, setDisplayTutorial] = useState(false);

  const WantCreatedUserName = props.WantCreatedUserName;
  const WantCreatedUserId = props.WantCreatedUserId;
  const wantAdClientId = props.wantAdClientId;
  const wantAdDetails = props.wantAdDetails;
  const wantCreatedFirstName = props.wantAdDetails?.userFirstName;

  const [selectedProperty, setSelectedProperty] = useState("");
  const [formData, setFormData] = useState("");
  const [memberIntroduction, setMemberIntroduction] = useState("");

  const [showReviewOrderModal, setReviewOrderModal] = useState(false);
  const ReviewOrderModalClose = () => setReviewOrderModal(false);
  const ReviewOrderModalShow = () => setReviewOrderModal(true);

  const [showSuccessModal, setSuccessModal] = useState(false);
  const SuccessModalClose = () => setSuccessModal(false);
  const SuccessModalShow = () => setSuccessModal(true);

  const [showIntroTutorialModal, setIntroTutorialModal] = useState(false);
  const IntroTutorialModalClose = () => setIntroTutorialModal(false);
  const IntroTutorialShow = () => setIntroTutorialModal(true);

  const [reload, setReload] = useState(0);
  const [messageCharCount, setMessageCharCount] = useState(0);
  const [msgError, setMsgError] = useState("");

  const [isPremiumUser, setIsPremiumUser] = useState(null);

  const userId = getUserId();
  const userDetails = loggedInUser();
  const userType = userDetails?.userInfo?.userType;

  const [isFree, setIsFree] = useState(false);
  let intoLinkDisplayStatus = checkTutorialDisplayStatus(tutorialPage);
  const [showIntroLink, setIntroLink] = useState(intoLinkDisplayStatus);
  const [isLoding, setIsLoading] = useState(false);

  const [messageCursorPos, setMessageCursorPos] = useState(0);

  let session = userType == 2 ? AGENT_INTRODUCTION : MEMBER_INTRODUCTION;

  // userDetailsFromStore state change side effect handling.
  useEffect(() => {
    if (isPremiumUser) {
      dispatch(getIntroClients(userId));
    }
  }, [userId, reload, isPremiumUser]);

  useEffect(() => {
    if (userDetailsFromStore !== null && userDetailsFromStore?.length > 0) {
      if (userDetailsFromStore[0]?.period !== 1 && userType === 2) {
        setIsPremiumUser(true);
      } else {
        setIsPremiumUser(false);
      }
    }
  }, [userDetailsFromStore]);

  // client id and isPremiumUser state change side effect handling.
  useEffect(() => {
    let params = {};

    if (userType == 2 && isPremiumUser) {
      if (formData?.clientId >= 0) {
        params = {
          wantAdId: wantAdId,
          clientId: formData?.clientId,
        };
        dispatch(fetchPropertyList(params));
      }
    } else {
      params = {
        wantAdId: wantAdId,
      };
      dispatch(fetchPropertyList(params));
    }
  }, [formData?.clientId, isPremiumUser]);

  // propertyList state change side effect handling.
  useEffect(() => {
    setSelectedProperty(defaultProperty);
    let initailData = {
      userId: userId,
      wantAdId: wantAdId,
      defaultAskingPrice: defaultProperty?.AskingPrice,
      selectedProperty: defaultProperty,
      inventoryId: defaultProperty?.Inv_Id,
    };
    setFormData((formData) => ({ ...formData, ...initailData }));
    handlePropertyChange(defaultProperty);
  }, [propertyList]);

  // clientList state change side effect handling.
  useEffect(() => {
    setSelectedClient(defaultClient);
    let initailData = {
      selectedClient: defaultClient,
      clientId: defaultClient?.ClientId,
    };
    setFormData((formData) => ({ ...formData, ...initailData }));
  }, [clientList]);

  // formData state change side effect handling.
  useEffect(() => {
    setLocalStorage(session, formData);
    setMemberIntroduction(getLocalStorage(session));
  }, [formData]);

  // formData inventroryid state change side effect handling.
  useEffect(() => {
    if (formData?.inventoryId) {
      dispatch(fetchInventoryDetailsById(formData?.inventoryId));
    }
  }, [formData?.inventoryId]);

  // messageCursorPos state change side effect handling.
  useEffect(() => {
    if (messageRef.current) {
      messageRef.current.setSelectionRange(messageCursorPos, messageCursorPos);
    }
  }, [messageCursorPos]);

  // To handle the textbox event
  const onChangeText = (event) => {
    let value = handleTextBoxChange(event);
    if (event.target.name == "message") {
      value = upperCaseEachSentenceFirst(value);
      setMessageCharCount(value.length);
      if (trimString(value) !== "" && textValidations(value)) {
        setMsgError("");
        setMessageCursorPos(event.target.selectionStart);
      } else {
        setMsgError(trimString(value) !== "" ? ERROR_TEXT : "");
      }
    }

    setFormData((formData) => ({ ...formData, [event.target.name]: value }));
  };

  // Property selection change event handling
  const handlePropertyChange = (property) => {
    setSelectedProperty(property);
    setFormData((formData) => ({
      ...formData,
      inventoryId: property?.Inv_Id,
      selectedProperty: property,
      defaultAskingPrice: property?.AskingPrice,
      askingPrice: "",
      message: "",
    }));
  };

  // inventoryDetails state change side effect handling.
  useEffect(() => {
    let photosAfterRemovingInvalid =
      inventoryDetails?.photos &&
      inventoryDetails?.photos.filter((item) => item.IsValid != 0);
    let photos =
      photosAfterRemovingInvalid &&
      photosAfterRemovingInvalid.map((photo, index) => {
        return { ...photo, order: index + 1 };
      });

    let inventoryData = {
      originalPhotos: photos, // data from db to customize
      photosForPreview: photos, // data from db to customize
      originalpropertyFeatures: inventoryDetails?.nice2Have, // data from db to customize
      propertyFeaturesForPreview: inventoryDetails?.nice2Have, // data from db to customize
      noBedRm: inventoryDetails?.noBedRm,
      noBathRm: inventoryDetails?.noBathRm,
      sqFt: inventoryDetails?.sqFt,
      lotSize: inventoryDetails?.lotSize,
      locAddress: inventoryDetails?.locAddress,
      salesPercnt: inventoryDetails?.salesPercnt,
      insReport: inventoryDetails?.insReport,
      apprOnFile: inventoryDetails?.apprOnFile,
      floodCert: inventoryDetails?.floodCert,
      s3ImageFolder: inventoryDetails?.s3ImageFolder,
      propertyTypeId: inventoryDetails?.prprtyType,
      acreage: inventoryDetails?.acreage,
      lotType: inventoryDetails?.lotType,
      timeLineTxt: inventoryDetails?.timeLineTxt,
      floorPlan: inventoryDetails?.floorPlan,
      sellerFin: inventoryDetails?.sellerFin,
      mapImg: inventoryDetails?.mapImg,
      city: inventoryDetails?.city,
      state: inventoryDetails?.state,
      zip: inventoryDetails?.zip,
      lat: inventoryDetails?.lat,
      lng: inventoryDetails?.lng,
    };

    if (inventoryDetails?.s3ImageFolder == "") {
      getFolderName();
    }

    setFormData((formData) => ({ ...formData, ...inventoryData }));
  }, [inventoryDetails]);

  // To get the folder name for image upload
  const getFolderName = async () => {
    const isWantAd = false;
    await createS3Folder(isWantAd).then(function (result) {
      let folderName = {
        s3ImageFolder: result,
      };
      setFormData((formData) => ({ ...formData, ...folderName }));
    });
  };

  // reload state update
  const updateReloadParam = () => {
    setReviewOrderModal(true);
  };

  // client reload state update
  const updateClientReload = () => {
    setReload(reload + 1);
  };

  // card selection event handling
  const updateCardParam = (paymentMethod) => {
    let cardData = {
      cardId: paymentMethod?.Payment_Mode_Id,
      cardData: paymentMethod,
    };
    setFormData((formData) => ({ ...formData, ...cardData }));
  };

  // isAdhereFFHAct checkbox event handling
  const handleCheckBoxItemClick = (e) => {
    setFormData((formData) => ({
      ...formData,
      isAdhereFFHAct: e.target.checked,
    }));
  };

  // isRepresentOwner checkbox event handling
  const handleIsRepresentOwnerCheckBoxItemClick = (e) => {
    setFormData((formData) => ({
      ...formData,
      isRepresentOwner: e.target.checked,
    }));
  };

  // on final save of customize intro modal
  const onSavePropertyFeatureOrder = (orderdProperty) => {
    let selectedCoverPhotos = memberIntroduction?.photosForPreview?.filter(
      (item) => item.isCover == 1,
    );
    let newArray = {
      coverPhotos: selectedCoverPhotos,
      propertyFeatures: orderdProperty,
      originalpropertyFeatures: orderdProperty,
    };
    setFormData((formData) => ({ ...formData, ...newArray }));
    setReviewOrderModal(true);
  };

  // On saving the customize intro uploaded image order change
  const SavePhotoOrder = (photoOrder) => {
    let selectedCoverPhotos = photoOrder?.filter((item) => item.isCover == 1);
    let coverPhotos = {
      photosForPreview: photoOrder,
    };
    setFormData((formData) => ({ ...formData, ...coverPhotos }));
  };

  // login to the firebase
  useEffect(() => {
    let email = _.get(userDetails, "userInfo.email", null);
    let login = loginOrSignUp(email);
  }, []);

  // On sending the introduction
  const SendIntroduction = async (isFreeOverride) => {
    setIsLoading(true);
    const free = isFreeOverride ? 1 : (isFree ? 1 : 0);
    let data = {
      wantAdId: wantAdId,
      wantAd_clientId: wantAdClientId,
      wantAd_UserId: WantCreatedUserId,
      inventoryId: memberIntroduction?.inventoryId,
      clientId: memberIntroduction?.clientId, // for agent
      askingPrice: memberIntroduction?.askingPrice
        ? memberIntroduction?.askingPrice
        : memberIntroduction?.defaultAskingPrice,
      message: memberIntroduction?.message,
      cardId: memberIntroduction?.cardId,
      amount: INTRODUCTION_AMOUNT,
      tax: INTRODUCTION_TAX,
      total:
        convertToNumber(INTRODUCTION_AMOUNT) +
        convertToNumber(INTRODUCTION_TAX),
      coverPhotos: memberIntroduction?.coverPhotos
        ? memberIntroduction?.coverPhotos
        : memberIntroduction?.originalPhotos,
      propertyFeatures: memberIntroduction?.propertyFeatures
        ? memberIntroduction?.propertyFeatures
        : memberIntroduction?.originalpropertyFeatures,
      s3ImageFolder: memberIntroduction?.s3ImageFolder,

      // data for firebase
      WantAdText: wantAdDetails?.adTitle,
      wantAdLocation: wantAdDetails?.location,
      wantAdClientName: wantAdDetails?.clientName
        ? wantAdDetails?.clientName
        : "",
      wantAdCreatedUserFirstName: wantAdDetails?.userFirstName,
      wantAdCreatedUserLastName: wantAdDetails?.userLastName,
      wantAdCreatedId: wantAdDetails?.userId,
      inventroyCreatedId: _.get(userDetails, "userInfo.userId", null),
      inventroyUserName: _.get(userDetails, "userInfo.name", null),
      lastestMessageSentUserId: _.get(userDetails, "userInfo.userId", null),
      wantAdPrprtyType: wantAdDetails?.prprtyType,
      isRepiled: false,
      isDeleted: false,
      isAccepted: false,
      acceptedTime: "",
      inventoryPhoto: memberIntroduction?.selectedProperty?.photo,
      inventoryLocation: memberIntroduction?.locAddress,
      inventoryTypeId: memberIntroduction?.propertyTypeId,
      isFree: free,
      isAdhereFFHAct: isFreeOverride ? 1 : (memberIntroduction?.isAdhereFFHAct == true ? 1 : 0),
      isRepresentOwner: isFreeOverride ? 1 : (memberIntroduction?.isRepresentOwner == true ? 1 : 0),
    };
    const sentInvId = memberIntroduction?.inventoryId;
    await saveIntroductions(data)
      .then((response) => {
        setIsLoading(false);
        if (response.errorCode == 0) {
          if (response.result.status == "created") {
            setReviewOrderModal(false);
            setFormData((formData) => ({
              ...formData,
              message: "",
              askingPrice: "",
            }));
            removeLocalStorage(session);

            if (isFreeOverride) {
              setBlsSentInvIds((prev) => {
                const next = new Set(prev);
                if (sentInvId) next.add(sentInvId);
                return next;
              });
            } else {
              setSuccessModal(true);
            }
            let uploadImages = data.coverPhotos.length;
            let inValidImages = uploadImages - response.result?.validImageCount;
            if (inValidImages > 0) {
              let message =
                "Oops! You’ve uploaded invalid images. One or more images that you have uploaded for the following property do not conform to our image policies. Invalid images are not included in this introduction.";
              toast.error(message);
            }
          } else {
            toast.error(response.message);
          }
        } else {
          toast.error("Something went wrong sending the introduction.");
        }
      })
      .catch((err) => {
        setIsLoading(false);
        console.log("Error", err);
        toast.error("Something went wrong sending the introduction.");
      });
  };

  // on saving the property features after reorder
  const savePropertyFeaturesforPreview = (orderdProperty) => {
    let newArray = {
      propertyFeaturesForPreview: orderdProperty,
    };
    setFormData((formData) => ({ ...formData, ...newArray }));
  };

  // customize intro modal close
  const setCustomizeIntroCancel = () => {
    setReviewOrderModal(true);
    let newArray = {
      propertyFeaturesForPreview: memberIntroduction?.originalpropertyFeatures,
      photosForPreview: memberIntroduction?.originalPhotos,
    };
    setFormData((formData) => ({ ...formData, ...newArray }));
  };


  const acceptTutorial = () => {
    setIntroTutorialModal(false);
    setDisplayTutorial(true);
  };
  const [tutorialSteps, setTutorialSteps] = useState([
    {
      target: "#propertySelection",
      disableBeacon: true,
      placement: "left",
      title: "Choose a property to share",
      icon: IntroductionTutorialIconStep1,
      content:
        "If you have multiple properties, you can swap out which one you want to introduce.",
      hideCloseButton: true,
      showProgress: true,
      showSkipButton: true,
    },
    {
      target: ".property-price",
      disableBeacon: true,
      placement: "bottom",
      title: "Adjust asking price",
      icon: IntroductionTutorialIconStep2,
      content:
        "This is just conversation starter. So try not to overthink it. You can always change it later.",
      hideCloseButton: true,
      showProgress: true,
      showSkipButton: true,
    },
    {
      target: ".property-message",
      disableBeacon: true,
      placement: "top",
      title: "Write a custom message",
      icon: IntroductionTutorialIconStep3,
      content:
        "This is your first chance to tell them why you think your property is a perfect match.",
      hideCloseButton: true,
      showProgress: true,
      showSkipButton: true,
    },
    {
      target: ".sendMessageBtn",
      disableBeacon: true,
      placement: "left",
      title: "Review and send",
      icon: IntroductionTutorialIconStep4,
      content:
        "You'll get another chance to review your introduction before you finalize and send.",
      hideCloseButton: true,
      showProgress: true,
    },
  ]);
  //Tutorial tooltip
  const TutorialToolTip = ({
    continuous,
    index,
    step,
    backProps,
    closeProps,
    primaryProps,
    tooltipProps,
    size,
    isLastStep,
    skipProps,
  }) => (
    <>
      <div {...tooltipProps}>
        <DocumentationTutorial
          continuous={continuous}
          index={index}
          step={step}
          backProps={backProps}
          skipProps={skipProps}
          primaryProps={primaryProps}
          isLastStep={isLastStep}
          size={size}
          finalStepBtn={"Got It!"}
        />
      </div>
    </>
  );

  //Tutorial status call back
  const handleJoyrideCallback = async (data) => {
    const { action, index, status, type, lifecycle } = data;
    if (
      [STATUS.FINISHED, STATUS.SKIPPED].includes(status) &&
      lifecycle == "complete"
    ) {
      setDisplayTutorial(false);
      setIntroLink(false);
      saveTutorialStatusToLocal(tutorialPage);
      await saveTutorialStatus(tutorialPage, 1); //Save status
    }
  };

  const isAgent = getUserType() === USER_TYPE_AGENT;

  const disabledSendIntroductionButton = selectedProperty?.Inv_Id &&
    !msgError &&
    (isAgent || selectedProperty?.photos?.length) &&
    ((selectedProperty?.IsIntroSent == 1 &&
      selectedProperty?.IsCancelled == 1) ||
      selectedProperty?.IsIntroSent == 0 ||
      !selectedProperty?.IsIntroSent) &&
    (selectedProperty?.IsDecline == 0 ||
      !selectedProperty?.IsDecline) &&
    (selectedProperty?.NotInterested == 0 ||
      !selectedProperty?.NotInterested) &&
    (selectedProperty?.Not_A_Fit == 0 || !selectedProperty?.Not_A_Fit)
    ? false
    : true

  const disabledFormInputs = selectedProperty?.Inv_Id &&
    (isAgent || selectedProperty?.photos?.length) &&
    ((selectedProperty?.IsIntroSent == 1 &&
      selectedProperty?.IsCancelled == 1) ||
      selectedProperty?.IsIntroSent == 0 ||
      !selectedProperty?.IsIntroSent) &&
    (selectedProperty?.IsDecline == 0 ||
      !selectedProperty?.IsDecline) &&
    (selectedProperty?.NotInterested == 0 ||
      !selectedProperty?.NotInterested) &&
    (selectedProperty?.Not_A_Fit == 0 || !selectedProperty?.Not_A_Fit)
    ? false
    : true


  const { shouldRestrictAgent } = useAgentGitchaRestrictions();

  if (shouldRestrictAgent) {
    return null;
  }

  return (
    <>
      {displayTutorial ? (
        <JoyRide
          steps={tutorialSteps}
          tooltipComponent={TutorialToolTip}
          continuous
          callback={handleJoyrideCallback}
          disableScrolling={true}
        />
      ) : (
        ""
      )}
      <div className="ad-strengthen-wrap mb-0 mb-sm-4">
        <div className="icon-wrap">
          <img src={AdIntroIcon} alt={AdIntroIcon} />
        </div>
        <h6 className="send-instruction-head">
          Send {wantCreatedFirstName} Your Listing
        </h6>
        <p className="mb-4 wanted-info-listdetail">
          Does your listing sound like a good match?
          <br />
          Introduce it!
        </p>

        <PropertySelection
          selectedProperty={memberIntroduction}
          propertyList={propertyList}
          handlePropertyChange={(property) => {
            handlePropertyChange(property);
          }}
          propertyListExist={propertyListExist}
          userType={userType}
          wantAdId={wantAdId}
        />

        <div className="addyour-property-details-wrap mb-12 property-message">
          <label className="form-label">Message (optional)</label>
          <TextArea
            name="message"
            id="message"
            className="form-control"
            errorMessage={msgError}
            value={formData?.message ? formData?.message : ""}
            maxLength={450}
            placeholder={`Hi ${WantCreatedUserName}, this property might be a great fit for your buyers. Let me know if they'd like to see it.`}
            onChange={(e) => {
              onChangeText(e);
            }}
            txtRef={messageRef}
            disabled={disabledFormInputs}
          />
          <div className="des-count align-self-end">{messageCharCount}/450</div>
        </div>

        {(() => {
          const currentInvId = memberIntroduction?.inventoryId;
          const blsSentForCurrent = currentInvId && blsSentInvIds.has(currentInvId);
          const blsLoading = isLoding;
          const showSent = blsSentForCurrent;
          const showSending = blsLoading && !showSent;
          const buttonDisabled = disabledSendIntroductionButton || showSending || showSent;
          const baseStyle = !disabledSendIntroductionButton
            ? { backgroundColor: 'white', color: 'var(--secondary-500)', border: '1px solid var(--secondary-500)', height: 40, fontSize: 14, fontWeight: 700 }
            : {};
          const sentStyle = showSent
            ? { backgroundColor: 'var(--success-100, #E6F7EC)', color: 'var(--success-700, #1B7A3E)', border: '1px solid var(--success-500, #2BA85F)', height: 40, fontSize: 14, fontWeight: 700, opacity: 1 }
            : baseStyle;
          return (
            <Button
              variant="primary"
              className="w-100 mb-0 l-spacing-0 sendMessageBtn"
              style={sentStyle}
              disabled={buttonDisabled}
              onClick={(e) => {
                e.preventDefault();
                SendIntroduction(true);
              }}
            >
              {showSent ? "✓ Introduction Sent" : showSending ? "Sending..." : "Send Introduction"}
            </Button>
          );
        })()}

      </div>
      <IntroTutorialModal
        show={showIntroTutorialModal}
        onHide={IntroTutorialModalClose}
        accept={acceptTutorial}
      />

      <ReviewOrderModal
        show={showReviewOrderModal}
        onHide={ReviewOrderModalClose}
        updateReloadParam={() => {
          updateReloadParam();
        }}
        selectedProperty={memberIntroduction}
        updateCardParam={(paymentMethod) => updateCardParam(paymentMethod)}
        handleCheckBoxItemClick={(e) => handleCheckBoxItemClick(e)}
        handleIsRepresentOwnerCheckBoxItemClick={(e) =>
          handleIsRepresentOwnerCheckBoxItemClick(e)
        }
        SendIntroduction={() => SendIntroduction()}
        WantCreatedUserName={WantCreatedUserName}
        onSavePropertyFeatureOrder={(orderdProperty) => {
          onSavePropertyFeatureOrder(orderdProperty);
        }}
        SavePhotoOrder={(photoOrder) => {
          SavePhotoOrder(photoOrder);
        }}
        viewReviewModal={() => {
          setCustomizeIntroCancel();
        }}
        savePropertyFeaturesforPreview={(orderdProperty) => {
          savePropertyFeaturesforPreview(orderdProperty);
        }}
        setIsFree={setIsFree}
        isLoding={isLoding}
      />

      <SuccessModal show={showSuccessModal} onHide={SuccessModalClose} />
    </>
  );
};
export default SendIntroduction;
